import { Pencil, Trash2, Clock, Package, AlertTriangle } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { prestazioniAPI } from '../../services/api';
import PrestazioneForm from './PrestazioneForm';
import DistintaPrestazione from './DistintaPrestazione';
import { magazzinoAPI } from '../../services/magazzinoApi';
import { useAuthStore } from '../../store/useStore';
import '../../styles/PrestazioniList.css';
import '../../styles/PrestazioniLinguette.css';
import { notificaErrore, messaggioDa } from '../../utils/notifiche';

/**
 * Catalogo — due linguette, servizi e prodotti.
 *
 * Con 570 servizi e 421 beni una lista sola non si governa: le colonne
 * che contano sono diverse. Per un servizio interessa chi lo esegue,
 * quanto dura e se e' prenotabile; per un prodotto il costo d'acquisto
 * e la gestione a magazzino.
 *
 * Il form resta uno solo: e' la stessa tabella, cambia la vista.
 */

const PrestazioniList = () => {
  const [prestazioni, setPrestazioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategoria, setFilterCategoria] = useState('tutte');
  const [filterAttivo, setFilterAttivo] = useState('attive');
  const [soloPrenotabili, setSoloPrenotabili] = useState(false);
  const [linguetta, setLinguetta] = useState('servizio');
  const [showForm, setShowForm] = useState(false);
  const [selectedPrestazione, setSelectedPrestazione] = useState(null);

  /* La distinta la gestisce il front-desk: le rotte del magazzino sono
     riservate ad admin e segreteria, e un pulsante che risponde 403 e'
     peggio di un pulsante che non c'e'. */
  const utente = useAuthStore((state) => state.user);
  const puoMagazzino = ['admin', 'segreteria'].includes(utente?.ruolo);
  /* Il listino si legge da tutti - serve per prenotare - ma i prezzi li
     cambia solo chi ne risponde, e la rotta risponde 403 a un medico.
     Un pulsante che porta a un rifiuto insegna a diffidare dei pulsanti:
     a chi non puo' scrivere non si mostra. */
  const puoModificare = puoMagazzino;
  const [distintaDi, setDistintaDi] = useState(null);
  const [distinte, setDistinte] = useState({});
  /* Gli identificativi delle prestazioni che nominano un iniettabile e
     non hanno distinta. L'elenco dei nomi commerciali sta SOLO nel
     backend: tenerne una copia qui vorrebbe dire due elenchi che il
     giorno del listino nuovo divergono, e la schermata segnalerebbe
     cose diverse da quelle che segnala il magazzino. */
  const [daCompletare, setDaCompletare] = useState(new Set());

  useEffect(() => {
    loadPrestazioni();
  }, []);

  /* Quante prestazioni consumano prodotti: una chiamata sola invece di
     una per riga, perche' la lista ne mostra centinaia. */
  useEffect(() => {
    if (!puoMagazzino) return;
    magazzinoAPI.distinte()
      .then((r) => setDistinte(r.data.data.distinte || {}))
      .catch((e) => console.error('Distinte:', e));
    magazzinoAPI.senzaDistinta()
      .then((r) => setDaCompletare(new Set(
        (r.data.data.prestazioni || [])
          .filter((x) => x.nomina_iniettabile)
          .map((x) => x.id))))
      .catch((e) => console.error('Senza distinta:', e));
  }, [puoMagazzino]);

  const loadPrestazioni = async () => {
    try {
      setLoading(true);
      const response = await prestazioniAPI.getAll();
      if (response.data.success) {
        setPrestazioni(response.data.data.prestazioni || []);
      }
      setError(null);
    } catch (err) {
      console.error('Errore caricamento prestazioni:', err);
      setError('Errore nel caricamento del catalogo');
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setSelectedPrestazione(null);
    setShowForm(true);
  };

  const handleEdit = (prestazione) => {
    setSelectedPrestazione(prestazione);
    setShowForm(true);
  };

  const handleDelete = async (prestazione) => {
    if (!window.confirm(`Eliminare "${prestazione.nome}"?\n\nQuesta operazione non è reversibile.`)) return;
    try {
      await prestazioniAPI.delete(prestazione.id);
      await loadPrestazioni();
    } catch (err) {
      console.error('Errore eliminazione:', err);
      notificaErrore(messaggioDa(err, 'Non riesco a eliminare la prestazione'));
    }
  };

  const handleFormSave = async () => {
    setShowForm(false);
    setSelectedPrestazione(null);
    await loadPrestazioni();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setSelectedPrestazione(null);
  };

  const formatPrezzo = (p) =>
    p != null && p !== '' ? `€ ${parseFloat(p).toFixed(2)}` : '-';

  /* Le voci senza tipologia si considerano servizi: e' il valore di
     default sulla tabella, e nessuna riga dovrebbe averla vuota. */
  const tipoDi = (p) => (p.tipologia === 'bene' ? 'bene' : 'servizio');

  const diQuestaLinguetta = prestazioni.filter((p) => tipoDi(p) === linguetta);
  const nServizi = prestazioni.filter((p) => tipoDi(p) === 'servizio').length;
  const nProdotti = prestazioni.filter((p) => tipoDi(p) === 'bene').length;

  // Aree in ordine di macro_area_ordine, non alfabetico: e' l'ordine
  // deciso sulla tabella macro_aree, lo stesso del controllo di gestione.
  const categorie = [...new Map(
    diQuestaLinguetta
      .filter((p) => p.macro_area)
      .map((p) => [p.macro_area, p.macro_area_ordine ?? 999])
  ).entries()].sort((x, y) => x[1] - y[1]).map(([nome]) => nome);

  const filtered = diQuestaLinguetta.filter((p) => {
    const term = searchTerm.toLowerCase().trim();
    const matchSearch =
      term === '' ||
      (p.nome || '').toLowerCase().includes(term) ||
      (p.codice || '').toLowerCase().includes(term) ||
      (p.descrizione || '').toLowerCase().includes(term) ||
      (p.medico_cognome || '').toLowerCase().includes(term) ||
      (p.medico_nome || '').toLowerCase().includes(term);

    const matchCategoria =
      filterCategoria === 'tutte' || p.macro_area === filterCategoria;

    const matchAttivo =
      filterAttivo === 'tutti' ||
      (filterAttivo === 'attive' && p.attivo) ||
      (filterAttivo === 'inattive' && !p.attivo);

    const matchPrenotabile =
      !soloPrenotabili || linguetta !== 'servizio' || p.prenotabile === true;

    return matchSearch && matchCategoria && matchAttivo && matchPrenotabile;
  });

  const cambiaLinguetta = (nuova) => {
    setLinguetta(nuova);
    setFilterCategoria('tutte');
    if (nuova !== 'servizio') setSoloPrenotabili(false);
  };

  if (loading) {
    return <div className="prestazioni-loading">Caricamento catalogo…</div>;
  }

  const servizi = linguetta === 'servizio';

  return (
    <div className="prestazioni-container">
      <div className="prestazioni-header">
        <h2>Catalogo</h2>
        {puoModificare && (
          <button className="btn-primary" onClick={handleNew}>
            {servizi ? 'Nuova prestazione' : 'Nuovo prodotto'}
          </button>
        )}
      </div>

      <div className="cat-linguette">
        <button
          className={`cat-linguetta ${servizi ? 'attiva' : ''}`}
          onClick={() => cambiaLinguetta('servizio')}
        >
          Prestazioni <span>{nServizi}</span>
        </button>
        <button
          className={`cat-linguetta ${!servizi ? 'attiva' : ''}`}
          onClick={() => cambiaLinguetta('bene')}
        >
          Prodotti <span>{nProdotti}</span>
        </button>
      </div>

      <div className="prestazioni-toolbar">
        <input
          type="text"
          className="search-input"
          placeholder={servizi
            ? 'Cerca per nome, codice, descrizione, medico…'
            : 'Cerca per nome, codice, descrizione…'}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="filter-select"
          value={filterCategoria}
          onChange={(e) => setFilterCategoria(e.target.value)}
        >
          <option value="tutte">Tutte le aree</option>
          {categorie.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          className="filter-select"
          value={filterAttivo}
          onChange={(e) => setFilterAttivo(e.target.value)}
        >
          <option value="tutti">Tutte</option>
          <option value="attive">Solo attive</option>
          <option value="inattive">Solo inattive</option>
        </select>

        {servizi && (
          <label className="cat-spunta">
            <input
              type="checkbox"
              checked={soloPrenotabili}
              onChange={(e) => setSoloPrenotabili(e.target.checked)}
            />
            <span>Solo prenotabili</span>
          </label>
        )}

        <div className="prestazioni-count">
          {filtered.length} {servizi
            ? `prestazion${filtered.length === 1 ? 'e' : 'i'}`
            : `prodott${filtered.length === 1 ? 'o' : 'i'}`}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {filtered.length === 0 ? (
        <div className="no-results">
          {diQuestaLinguetta.length === 0
            ? `Nessun${servizi ? 'a prestazione inserita' : ' prodotto inserito'}.`
            : 'Niente che corrisponda ai filtri attuali.'}
        </div>
      ) : (
        <div className="prestazioni-table-wrapper">
          <table className="prestazioni-table">
            <thead>
              {servizi ? (
                <tr>
                  <th>Codice</th>
                  <th>Nome</th>
                  <th>Area</th>
                  <th>Medico</th>
                  <th className="text-right">Prezzo</th>
                  <th className="text-center">IVA</th>
                  <th className="text-center">Durata</th>
                  <th className="text-center">Agenda</th>
                  <th>Stato</th>
                  <th>Azioni</th>
                </tr>
              ) : (
                <tr>
                  <th>Codice</th>
                  <th>Nome</th>
                  <th>Area</th>
                  <th className="text-right">Prezzo</th>
                  <th className="text-right">Costo</th>
                  <th className="text-center">IVA</th>
                  <th className="text-center">Magazzino</th>
                  <th>Stato</th>
                  <th>Azioni</th>
                </tr>
              )}
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className={!p.attivo ? 'prestazione-inattiva' : ''}>
                  <td className="codice-cell">{p.codice || '-'}</td>
                  <td>
                    <strong>{p.nome}</strong>
                    {/* L'etichetta e la scadenza si vedono qui perche' e'
                        da qui che si cambiano: la schermata dei pacchetti
                        speciali li mostra soltanto. */}
                    {p.pacchetto_speciale && (
                      <span className="pacchetto-tag">
                        Pacchetto speciale
                        {p.pacchetto_scadenza
                          ? ` · scade il ${new Date(String(p.pacchetto_scadenza).slice(0, 10)).toLocaleDateString('it-IT')}`
                          : ''}
                      </span>
                    )}
                    {p.descrizione && <div className="descrizione">{p.descrizione}</div>}
                  </td>
                  <td>{p.macro_area || '-'}</td>

                  {servizi && (
                    <td>
                      {p.medico_cognome
                        ? <span className="medico-tag">Dr. {p.medico_cognome} {p.medico_nome}</span>
                        : <span className="medico-generico">— Generica</span>}
                    </td>
                  )}

                  <td className="text-right">{formatPrezzo(p.prezzo_indicativo)}</td>

                  {!servizi && (
                    <td className="text-right costo-cell">
                      {p.costo_prodotto != null && parseFloat(p.costo_prodotto) > 0
                        ? formatPrezzo(p.costo_prodotto)
                        : '-'}
                    </td>
                  )}

                  <td className="text-center">
                    {p.iva != null ? `${parseFloat(p.iva).toFixed(0)}%` : '-'}
                  </td>

                  {servizi ? (
                    <>
                      <td className="text-center">
                        {p.durata_minuti ? `${p.durata_minuti}′` : '-'}
                      </td>
                      <td className="text-center">
                        {p.prenotabile
                          ? <span className="cat-si" title="Prenotabile in agenda">
                              <Clock size={14} strokeWidth={1.9} />
                            </span>
                          : <span className="cat-no">—</span>}
                      </td>
                    </>
                  ) : (
                    <td className="text-center">
                      {/* Non piu' il vecchio flag gestito_magazzino, che dopo la
                          separazione fra prestazioni e prodotti non vuol dire
                          piu' niente: conta se la prestazione ha una distinta,
                          cioe' se eseguirla muove davvero il magazzino. */}
                      {distinte[p.id]
                        ? <span className="cat-si" title={`Consuma ${distinte[p.id]} prodotti`}>
                            <Package size={14} strokeWidth={1.9} />
                          </span>
                        /* Un trattino dice «non consuma niente», e per una
                           prestazione che nel nome porta un filler non e'
                           vero: vuol dire «non lo sappiamo ancora». Le due
                           cose si vedevano uguali, e sono 61. */
                        : daCompletare.has(p.id)
                        ? <span className="cat-manca"
                                title="Nomina un iniettabile ma non dice quale prodotto consuma: il numero di lotto non ha dove andare">
                            <AlertTriangle size={14} strokeWidth={1.9} />
                          </span>
                        : <span className="cat-no">—</span>}
                    </td>
                  )}

                  <td>
                    <span className={`stato-badge ${p.attivo ? 'attivo' : 'inattivo'}`}>
                      {p.attivo ? 'Attiva' : 'Inattiva'}
                    </span>
                  </td>
                  <td className="azioni-cell">
                    {puoMagazzino && (
                      <button
                        className={`btn-action btn-distinta${distinte[p.id] ? ' ha-distinta' : ''}`}
                        onClick={() => setDistintaDi(p)}
                        title={distinte[p.id]
                          ? `Consuma ${distinte[p.id]} prodotti di magazzino`
                          : 'Collega i prodotti consumati'}>
                        <Package size={15} strokeWidth={1.8} aria-hidden="true" />
                      </button>
                    )}
                    {puoModificare && (
                      <>
                        <button className="btn-action btn-edit" onClick={() => handleEdit(p)} title="Modifica">
                          <Pencil size={15} strokeWidth={1.8} aria-hidden="true" />
                        </button>
                        <button className="btn-action btn-delete" onClick={() => handleDelete(p)} title="Elimina">
                          <Trash2 size={15} strokeWidth={1.8} aria-hidden="true" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <PrestazioneForm
          prestazione={selectedPrestazione}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
        />
      )}

      {distintaDi && (
        <DistintaPrestazione
          prestazione={distintaDi}
          onChiudi={() => setDistintaDi(null)}
          onSalvata={(msg, quanti) => {
            setDistinte((prev) => {
              const nuovo = { ...prev };
              if (quanti > 0) nuovo[distintaDi.id] = quanti;
              else delete nuovo[distintaDi.id];
              return nuovo;
            });
          }}
        />
      )}
    </div>
  );
};

export default PrestazioniList;
